import React, { useEffect, useState } from 'react';
import "./index.scss";
import DSSButton from '../../../../ui/building-blocks/atoms/dss-button';
import DSSTextInput from '../../../../ui/building-blocks/atoms/dss-textinput';
import DSSDropdown from '../../../../ui/building-blocks/atoms/dss-dropdown';
import { US_PHONE_REGEX } from '../../../../util/validation';
import { useDssContext } from '../../../../DSSContext';
import { getCookie, ID_TOKEN_KEY } from '../../../../interceptors/constant';
import { commonPostServiceApi } from '../../../../services/api-service';
import { ENDPOINTS } from '../../api/endpoint';
import DSSLoader from '../../../../ui/building-blocks/atoms/dss-loader/dss-loader';
import { notifyError, notifySuccess } from '../../../../ui/dls/dls-snackbar/dls-snackbar';
import { ProfileView } from '../../model/model';
import { SaveProfileData } from '../../api/api';

interface ProfileModalProps {
    onClose: () => void;
}

export const ProfileModal: React.FC<ProfileModalProps> = ({ onClose }) => {
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [editMode, setEditMode] = useState(false);
    // const [emailError, setEmailError] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [profileData, setProfileData] = useState<ProfileView | null>(null);
    const [loading, setLoading] = useState(false);
    const { translate, userDetail } = useDssContext();
    const customers = (userDetail?.customers) || [];
    const honewellId = (getCookie(ID_TOKEN_KEY) ?? '').toLowerCase();
    const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
    const handleEdit = () => setEditMode(true);

    const handleSave = async () => {
        let valid = true;

        // setEmailError('');
        // if (!email.trim()) {
        //     setEmailError(translate('EmailRequired'));
        //     valid = false;
        // } else if (!validateEmail(email)) {
        //     setEmailError(`${translate('enterValid')} ${translate('email')}`);
        //     valid = false;
        // }

        setPhoneError('');
        if (!phoneNumber.trim()) {
            setPhoneError(translate('PhoneNoRequired'));
            valid = false;
        } else if (!US_PHONE_REGEX.test(phoneNumber)) {
            setPhoneError(`${translate('enterValid')} ${translate('phoneNumber')}`);
            valid = false;
        }

        const selectedCustomerName = customers.find((c: any) => String(c.customerRefSeq) === selectedCustomerId)?.customerName
            ?? profileData?.customerName
            ?? '';
        if (valid) {
            const payload: ProfileView = {
                customerName: selectedCustomerName,
                firstName: profileData?.firstName,
                lastName: profileData?.lastName,
                emailAddress: email,
                phoneNumber: phoneNumber
            };
            try {
                setLoading(true);
                await SaveProfileData(payload);
                setEditMode(false);
                notifySuccess("Changes requested are mailed to Administrator successfully.");
                onClose();
            } catch (error: any) {
                notifyError(error || 'Failed to send mail');
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const payload = { honeywellOrEmailId: honewellId };
                const result: ProfileView = await commonPostServiceApi(ENDPOINTS.profileView, payload);
                setProfileData(result);
            } catch (err: any) {
                notifyError(err)
            } finally {
                setLoading(false);
            }
        };
        if (honewellId) fetchProfile();
    }, [honewellId]);
    useEffect(() => {
        if (!selectedCustomerId) {
            if (profileData?.customerName) {
                const match = customers.find((c: any) => c.customerName === profileData.customerName);
                if (match) {
                    setSelectedCustomerId(String(match.customerRefSeq));
                    return;
                }
            }
            if (customers.length > 0) {
                setSelectedCustomerId(String(customers[0].customerRefSeq));
            }
        }
    }, [customers, profileData, selectedCustomerId]);
    useEffect(() => {
        if (profileData) {
            setEmail(profileData.email ?? '');
            setPhoneNumber(profileData.phoneNumber ?? '');
        }
    }, [profileData]);

    return (
        <div className='profile-modal-content'>
            {loading && <DSSLoader id="" />}

            <div className="profile-modal-header">
                <span>{translate('profile')}</span>
            </div>

            {customers.length > 1 && (
                <div className="profile-row">
                    <DSSDropdown
                        id="profile-customer-dropdown"
                        name="profile-customer-dropdown"
                        label={translate('customer')}
                        value={selectedCustomerId}
                        className="profile-input"
                        onChange={(value: string) => setSelectedCustomerId(value)}
                        placeholder={translate('select') + ' ' + translate('customer')}
                        options={customers.map((c: any) => ({ value: String(c.customerRefSeq), label: c.customerName }))}
                        disabled={customers.length === 0}
                    />
                </div>
            )}

            <div className="profile-row">
                <span>
                    <DSSTextInput
                        id="customerName"
                        label={translate('customerName')}
                        value={(customers.find((c: any) => String(c.customerRefSeq) === selectedCustomerId)?.customerName
                            ?? profileData?.customerName
                            ?? '')}
                        disabled
                        type="text"
                        className="profile-input"
                    />
                </span>
                <span>
                    <DSSTextInput
                        id="firstName"
                        label={translate('firstName')}
                        value={profileData?.firstName ?? ''}
                        disabled
                        className="profile-input"
                        type="text"
                    />
                </span>
                <span>
                    <DSSTextInput
                        id="lastName"
                        label={translate('lastName')}
                        value={profileData?.lastName ?? ''}
                        disabled
                        type="text"
                        className="profile-input"
                    />
                </span>
            </div>

            <div className="profile-row">
                <span>
                    <DSSTextInput
                        id="email"
                        label={translate('email')}
                        value={email}
                        disabled
                        type="text"
                        onChange={e => setEmail(e.target.value)}
                        className="profile-input"
                    />
                </span>
                <span>
                    <DSSTextInput
                        id="phoneNumber"
                        label={translate('phoneNumber')}
                        value={phoneNumber}
                        disabled={!editMode}
                        type="text"
                        onChange={e => setPhoneNumber(e.target.value)}
                        className={`profile-input ${phoneError ? 'input-error' : ''}`}
                    />
                    <div className={phoneError ? 'profile-error' : 'profile-error profile-error-placeholder'}>{phoneError || ' '}</div>
                </span>
                <span></span>
            </div>

            <div className="profile-modal-actions">
                <DSSButton 
                    variant="secondary" 
                    className="close-profile-button" 
                    onClick={onClose} 
                    id="profileCloseBtn"
                >
                    {translate('close')}
                </DSSButton>
                <DSSButton
                    variant="primary" 
                    className={editMode ? "save-profile-button" : "edit-profile-button"}
                    onClick={editMode ? handleSave : handleEdit}
                    id="profileEditBtn"
                >
                    {translate(editMode ? 'save' : 'edit')}
                </DSSButton>
            </div>
        </div>
    );
};
